import { useMemo, useRef } from 'react';
import type { IDataTableGridOptions } from '@kognitos/lattice';
import { useScrollEdges } from '@/shared/hooks/use-scroll-edges';

/**
 * Scroll edges for an AG Grid. The grid scrolls its own inner viewports, which
 * exist only once it has rendered, so they are watched from grid events and
 * released when the grid tears itself down.
 *
 * @returns A ref for the element wrapping the grid, the grid events to pass in
 *   its options, and which edges have more to scroll
 */
export function useGridScrollEdges<TData>() {
  const containerRef = useRef<HTMLDivElement>(null);
  const detachRef = useRef<(() => void) | undefined>(undefined);
  const { edges, track } = useScrollEdges();

  const gridEvents = useMemo<IDataTableGridOptions<TData>>(() => {
    // Retried on later events: early ones can fire before the viewports render.
    const attach = () => {
      const root = containerRef.current;
      if (!root || detachRef.current) return;
      detachRef.current = track(
        root.querySelector<HTMLElement>('.ag-center-cols-viewport'),
        root.querySelector<HTMLElement>('.ag-body-viewport'),
      );
    };
    return {
      onGridReady: attach,
      onFirstDataRendered: attach,
      onDisplayedColumnsChanged: attach,
      onGridPreDestroyed: () => {
        detachRef.current?.();
        detachRef.current = undefined;
      },
    };
  }, [track]);

  return { containerRef, gridEvents, edges };
}
